import { HYDRATE } from "next-redux-wrapper";
import {
    createAction,
    createSlice,
    SliceCaseReducers,
    ValidateSliceCaseReducers
} from "@reduxjs/toolkit";
import { loadDefaultValues as loadDefaultValuesReducer } from "./postFormSlice.reducers";
import { FormFile, initialState, PostFormState } from "./postFormSlice.types";
import { createSimpleSetter, createValidatedSetter, validators } from "./postFormSlice.validators";
import { AppState } from "./store";
import { Brand, Generation, Model } from "./types";
import { checkIsFilled } from "./utils/checkIsFilled";

const hydrate = createAction<AppState>(HYDRATE);

export const postEditFormsReducers: ValidateSliceCaseReducers<
    PostFormState,
    SliceCaseReducers<PostFormState>
> = {
    updateStatus(
        state,
        action: {
            payload: {
                blockName: keyof PostFormState;
                isFilled: boolean;
            };
        }
    ) {
        (state as unknown as Record<string, unknown>)[action.payload.blockName as string] =
            !!action.payload.isFilled;
        state.isFilled = checkIsFilled(state);
    },
    reset: (state) => {
        state.selectedBrand = undefined;
        state.selectedModel = undefined;

        state.selectedGeneration = undefined;
        state.autoFilled = false;
        state.characteristicsFilled = false;
        state.photoFilled = false;
        state.descriptionFilled = false;
        state.contactsFilled = false;
        state.placeFilled = false;
        state.priceFilled = false;
        state.isFilled = false;
        state.selectedPics = [];
        state.selectedSellerType = "individual";
        state.selectedYear = undefined;
        state.selectedBodyType = undefined;
        state.selectedEngine = undefined;
        state.selectedDrive = undefined;
        state.selectedColor = undefined;
        state.selectedMileage = undefined;
        state.selectedDescription = undefined;
        state.selectedPrice = undefined;
        state.selectedCity = undefined;
        state.selectedCountry = undefined;
        state.selectedPhone = undefined;
        state.selectedName = undefined;
        state.selectedOwnersQnt = undefined;
        state.selectedTransmission = undefined;
        state.selectedWheel = undefined;
    },

    loadDefaultValues: loadDefaultValuesReducer,

    setBrand: (state, action: { payload: Brand }) => {
        const newBrand = action.payload;
        if (!newBrand || !state.selectedBrand || state.selectedBrand.id !== newBrand.id) {
            state.selectedModel = undefined;
            state.selectedGeneration = undefined;
        }
        state.selectedBrand = newBrand;
        state.isFilled = checkIsFilled(state);
    },
    setModel: (state, action: { payload: Model }) => {
        const newModel = action.payload;
        if (!newModel || !state.selectedModel || state.selectedModel.id !== newModel.id) {
            state.selectedGeneration = undefined;
        }
        state.selectedModel = newModel;
        state.isFilled = checkIsFilled(state);
    },
    setGeneration: (state, action: { payload: Generation }) => {
        state.selectedGeneration = action.payload;
        state.isFilled = checkIsFilled(state);
    },
    setSelectedEngine: createValidatedSetter("selectedEngine", "engine"),
    setSelectedDrive: createValidatedSetter("selectedDrive", "drive"),
    setBodyType: createValidatedSetter("selectedBodyType", "bodyType"),
    setYear: createValidatedSetter("selectedYear", "year"),
    setSelectedColor: createValidatedSetter("selectedColor", "color"),
    setSelectedDescription: createValidatedSetter("selectedDescription", "description"),
    setSelectedPrice: createValidatedSetter("selectedPrice", "price"),
    setSelectedCity: createSimpleSetter("selectedCity"),
    setSelectedCountry: createSimpleSetter("selectedCountry"),
    setSelectedStreet: createSimpleSetter("selectedStreet"),
    setSelectedPhone: createValidatedSetter("selectedPhone", "phone"),
    setSelectedName: createValidatedSetter("selectedName", "name"),
    setSelectedSellerType: (state, action: { payload: string }) => {
        state.selectedSellerType = action.payload;
        state.isFilled = checkIsFilled(state);
    },
    setSelectedMileage: (state, action: { payload: number }) => {
        const value = action.payload;
        const validation = validators.mileage(value);

        state.selectedMileage = value;
        state.mileageUpdated = true;
        state.mileageValid = validation.isValid;
        state.isFilled = checkIsFilled(state);
    },
    setSelectedPics: (state, action: { payload: FormFile[] }) => {
        state.selectedPics = action.payload;
        state.isFilled = checkIsFilled(state);
    },
    setSelectedOwnersQnt: (state, action: { payload: number }) => {
        const value = action.payload;
        const validation = validators.ownersQnt(value);

        state.selectedOwnersQnt = value;
        state.selectedOwnersQntUpdated = true;
        state.selectedOwnersQntValid = validation.isValid;
        state.horsePowerErrMessage = validation.errorMessage;
        state.isFilled = checkIsFilled(state);
    },
    setSelectedTransmission: createValidatedSetter("selectedTransmission", "transmission"),
    setSelectedWheel: createValidatedSetter("selectedWheel", "wheel"),
    setHorsePower: createValidatedSetter("horsePower", "horsePower"),
    setVin: createValidatedSetter("vin", "vin"),
    setCoordinates: (state, action: { payload: number[] }) => {
        state.coordinates = action.payload;
    }
};

export const postFormSlice = createSlice({
    name: "postForm",
    initialState,
    reducers: postEditFormsReducers,

    extraReducers: (builder) => {
        builder.addCase(hydrate, (state, action) => {
            return {
                ...state,
                ...action.payload.postForm
            };
        });
    }
});

export const {
    updateStatus,
    reset,
    setBrand,
    setModel,
    setGeneration,
    setSelectedEngine,
    setSelectedDrive,
    setBodyType,
    setYear,
    setSelectedColor,
    setSelectedDescription,
    setSelectedPrice,
    setSelectedCity,
    setSelectedCountry,
    setSelectedPhone,
    setSelectedName,
    setSelectedSellerType,
    setSelectedMileage,
    setSelectedStreet,
    setSelectedPics,
    setSelectedOwnersQnt,
    setSelectedTransmission,
    setSelectedWheel,
    setHorsePower,
    loadDefaultValues,
    setVin,
    setCoordinates
} = postFormSlice.actions;

export const actions = postFormSlice.actions;

export const selectFilledStatus = (state: AppState) => state.postForm;

export default postFormSlice.reducer;

export type { PostFormState, FormFile, EditPostStoreName } from "./postFormSlice.types";